import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const RESOURCE = "/onboarding";

export type OnboardingChecklistKey =
  | "unit_schedule_configured"
  | "first_professional"
  | "first_service_bundle"
  | "health_record_template"
  | "concierge_booked"
  | "aha_moment";

export type QuickstartStepKey =
  | "first_service"
  | "unit_schedule"
  | "first_appointment";

export interface OnboardingChecklistItem {
  key: OnboardingChecklistKey;
  done: boolean;
}

export interface QuickstartStep {
  key: QuickstartStepKey;
  done: boolean;
  cta: string;
  blocked_by_plan: boolean;
}

export interface QuickstartStatus {
  visible: boolean;
  dismissed: boolean;
  dismissed_at: string | null;
  completed_count: number;
  total: number;
  salon_live: boolean;
  booking_token: string | null;
  booking_url: string | null;
  steps: QuickstartStep[];
}

export interface ConciergeBannerStatus {
  visible: boolean;
  dismissed: boolean;
  dismissed_at: string | null;
}

export type UsageTipKey =
  | "first_service"
  | "unit_schedule"
  | "first_appointment"
  | "team"
  | "booking_link"
  | "extension"
  | "paid_order"
  | "reminder"
  | "mobile_app";

export type UsageTipAction = "navigate" | "copy_link" | "extension" | "play_store";

export interface UsageTip {
  key: UsageTipKey;
  action: UsageTipAction;
  path: string | null;
  url: string | null;
  article: string | null;
}

export interface UsageTipsStatus {
  dismissed: boolean;
  dismissed_at: string | null;
}

export interface WelcomeStatus {
  show: true;
}

export interface OnboardingStatus {
  concierge_gate_passed: boolean;
  concierge_booked_at: string | null;
  concierge_completed_at: string | null;
  aha_completed: boolean;
  aha_type: "first_paid_order" | "first_appointment" | null;
  trial_days_remaining: number;
  checklist: OnboardingChecklistItem[];
  quickstart: QuickstartStatus;
  concierge_banner: ConciergeBannerStatus;
  welcome?: WelcomeStatus | null;
  usage_tip?: UsageTip | null;
  usage_tips?: UsageTipsStatus;
}

export interface ConciergeSlot {
  id: number;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
}

export interface ConciergeBooking {
  id: number;
  status: string;
  booked_at: string | null;
  slot: {
    id: number;
    starts_at: string;
    ends_at: string;
    duration_minutes: number;
  };
}

export const getStatus = (): Promise<ApiResponse<OnboardingStatus>> =>
  api.get<ApiResponse<OnboardingStatus>>(`${RESOURCE}/status`);

export const getConciergeSlots = (): Promise<ApiResponse<ConciergeSlot[]>> =>
  api.get<ApiResponse<ConciergeSlot[]>>(`${RESOURCE}/concierge/slots`);

export const bookConcierge = (
  slotId: number
): Promise<ApiResponse<ConciergeBooking>> =>
  api.create<ApiResponse<ConciergeBooking>>(`${RESOURCE}/concierge/book`, {
    concierge_slot_id: slotId,
  });

export const cancelConcierge = (): Promise<ApiResponse<{ id: number; status: string }>> =>
  api.delete<ApiResponse<{ id: number; status: string }>>(`${RESOURCE}/concierge/book`);

export const dismissQuickstart = (): Promise<
  ApiResponse<{ quickstart: QuickstartStatus }>
> =>
  api.create<ApiResponse<{ quickstart: QuickstartStatus }>>(
    `${RESOURCE}/quickstart/dismiss`,
    {}
  );

export const restoreQuickstart = (): Promise<
  ApiResponse<{ quickstart: QuickstartStatus }>
> =>
  api.create<ApiResponse<{ quickstart: QuickstartStatus }>>(
    `${RESOURCE}/quickstart/restore`,
    {}
  );

export const dismissConciergeBanner = (): Promise<
  ApiResponse<{ concierge_banner: ConciergeBannerStatus }>
> =>
  api.create<ApiResponse<{ concierge_banner: ConciergeBannerStatus }>>(
    `${RESOURCE}/concierge-banner/dismiss`,
    {}
  );

export const restoreConciergeBanner = (): Promise<
  ApiResponse<{ concierge_banner: ConciergeBannerStatus }>
> =>
  api.create<ApiResponse<{ concierge_banner: ConciergeBannerStatus }>>(
    `${RESOURCE}/concierge-banner/restore`,
    {}
  );

export interface FirstExperiencePayload {
  welcome: WelcomeStatus | null;
  usage_tip: UsageTip | null;
  usage_tips: UsageTipsStatus;
}

export const completeWelcome = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/welcome/complete`, {});

export const skipWelcome = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/welcome/skip`, {});

export const replayWelcome = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/welcome/replay`, {});

export const dismissUsageTips = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/usage-tips/dismiss`, {});

export const restoreUsageTips = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/usage-tips/restore`, {});

export const markUsageTipSeen = (): Promise<ApiResponse<FirstExperiencePayload>> =>
  api.create<ApiResponse<FirstExperiencePayload>>(`${RESOURCE}/usage-tips/seen`, {});
